import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { 
  ArrowLeft, 
  Edit, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Star,
  Users,
  Calendar,
  Trash2
} from 'lucide-react';
import AdminNavbar from '../../../AdminNavbar';
import { clinicService } from '../../../../services';
import { useDataFetching } from '../../../../hooks';
import { toast } from 'sonner';

const AdminClinicView = () => {
  const { clinicId } = useParams();
  const [isDeleting, setIsDeleting] = useState(false);

  // Use custom hook for data fetching
  const { data: clinic, isLoading, setData: setClinic } = useDataFetching(
    () => clinicService.getClinicById(clinicId),
    null,
    { errorMessage: 'Failed to load clinic details.' }
  );

  const handleDeleteClinic = async () => {
    if (!clinicId || clinicId === 'undefined') {
      toast.error('Invalid clinic ID');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this clinic? This action cannot be undone.')) {
      setIsDeleting(true);
      try {
        await clinicService.deleteClinic(clinicId);
        toast.success('Clinic deleted successfully!');
        // Redirect to clinics list
        window.location.href = '/admin/clinics';
      } catch (error) {
        console.error('Error deleting clinic:', error);
        toast.error('Failed to delete clinic. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading clinic details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Clinic Not Found</h1>
            <p className="text-gray-600 mb-6">The clinic you're looking for doesn't exist or has been removed.</p>
            <Link to="/admin/clinics">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Clinics
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/admin/clinics">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Clinics
              </Button>
            </Link>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">{clinic.name}</h1>
              <p className="text-gray-600 mt-2">Clinic Details & Information</p>
            </div>
            <div className="flex gap-2">
              <Link to={`/admin/clinics/${clinicId}/edit`}>
                <Button>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Clinic
                </Button>
              </Link>
              <Button 
                variant="destructive" 
                onClick={handleDeleteClinic}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Clinic Name:</span>
                    </div>
                    <p className="text-gray-700 ml-6">{clinic.name}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Email:</span>
                    </div>
                    <p className="text-gray-700 ml-6">{clinic.email}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Phone:</span>
                    </div>
                    <p className="text-gray-700 ml-6">{clinic.phone}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">City:</span>
                    </div>
                    <p className="text-gray-700 ml-6">{clinic.city}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Rating:</span>
                    </div>
                    <p className="text-gray-700 ml-6">{clinic.rating}/5.0 ({clinic.totalReviews} reviews)</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Doctors:</span>
                    </div>
                    <p className="text-gray-700 ml-6">{clinic.doctors} doctors</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">Address:</span>
                  </div>
                  <p className="text-gray-700 ml-6">{clinic.address}</p>
                </div>

                {clinic.description && (
                  <div className="space-y-2">
                    <span className="font-medium">Description:</span>
                    <p className="text-gray-700">{clinic.description}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <span className="font-medium">Status:</span>
                  <div className="ml-0">
                    <Badge variant={clinic.status === 'active' ? "default" : "destructive"}>
                      {clinic.status.charAt(0).toUpperCase() + clinic.status.slice(1)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Specializations & Facilities */}
            <Card>
              <CardHeader>
                <CardTitle>Specializations & Facilities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {clinic.specializations && clinic.specializations.length > 0 && (
                  <div className="space-y-2">
                    <span className="font-medium">Specializations:</span>
                    <div className="flex flex-wrap gap-2">
                      {clinic.specializations.map((spec, index) => (
                        <Badge key={index} variant="secondary">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {clinic.facilities && clinic.facilities.length > 0 && (
                  <div className="space-y-2">
                    <span className="font-medium">Facilities:</span>
                    <div className="flex flex-wrap gap-2">
                      {clinic.facilities.map((facility, index) => (
                        <Badge key={index} variant="outline">
                          {facility}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Working Hours */}
            {clinic.workingHours && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Working Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {days.map((day) => (
                      <div key={day.key} className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="font-medium w-24">{day.label}</span>
                        <div className="flex-1 text-center">
                          {clinic.workingHours[day.key]?.closed ? (
                            <span className="text-red-600 font-medium">Closed</span>
                          ) : (
                            <span className="text-gray-700">
                              {clinic.workingHours[day.key]?.open} - {clinic.workingHours[day.key]?.close}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Doctors</span>
                  <span className="font-semibold">{clinic.doctors}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Rating</span>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span className="font-semibold">{clinic.rating}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Reviews</span>
                  <span className="font-semibold">{clinic.totalReviews}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <Badge variant={clinic.status === 'active' ? "default" : "destructive"}>
                    {clinic.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to={`/admin/clinics/${clinicId}/edit`} className="block">
                  <Button className="w-full">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Clinic
                  </Button>
                </Link>
                <Button 
                  variant="destructive" 
                  className="w-full"
                  onClick={handleDeleteClinic}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {isDeleting ? 'Deleting...' : 'Delete Clinic'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClinicView;
